// File names, saving/sharing files and clipboard access for exports.
import type { DocumentFull } from '../../../shared/types';
import { isAndroidApp, saveFileInApp } from '../lib/android';
import { DOC_TYPE_LABEL, formatDate, localDayKey, slugify } from '../lib/format';

/** How long the object URL of a download stays valid; the browser must have started reading it by then. */
const REVOKE_DELAY_MS = 10_000;

/** Letters that have no Unicode decomposition, so `slugify` would drop them entirely (Đorđević -> "or-evi"). */
const NON_DECOMPOSABLE: Record<string, string> = {
  đ: 'd',
  Đ: 'D',
  ł: 'l',
  Ł: 'L',
  ø: 'o',
  Ø: 'O',
  æ: 'ae',
  Æ: 'AE',
  œ: 'oe',
  Œ: 'OE',
  ß: 'ss',
};

function asciiSlug(text: string): string {
  const prepared = text.replace(/[đĐłŁøØæÆœŒß]/g, (ch) => NON_DECOMPOSABLE[ch] ?? ch);
  // slugify() falls back to "dokument" for empty input; an empty title should simply be left out.
  return /[A-Za-z0-9]/.test(prepared.normalize('NFD')) ? slugify(prepared) : '';
}

/**
 * "Sestanek-2026-10-24-Projekt-X.docx". The type word is not repeated when the title already starts
 * with it ("Nadzor Šola" -> "Nadzor-2026-10-24-Sola"), and a default title ("Nadzor 24. 10. 2026")
 * does not add the same date a second time.
 */
export function exportFileName(doc: DocumentFull, ext: string): string {
  const label = DOC_TYPE_LABEL[doc.type] ?? 'Dokument';
  const created = new Date(doc.createdAt);
  const day = Number.isNaN(created.getTime()) ? localDayKey(new Date()) : localDayKey(created);

  let slug = asciiSlug(doc.title);
  const lowerLabel = label.toLowerCase();
  if (slug.toLowerCase() === lowerLabel) slug = '';
  else if (slug.toLowerCase().startsWith(`${lowerLabel}-`)) slug = slug.slice(label.length + 1);

  const dateSlug = asciiSlug(formatDate(doc.createdAt)); // "24-10-2026"
  if (dateSlug && slug === dateSlug) slug = '';
  else if (dateSlug && slug.startsWith(`${dateSlug}-`)) slug = slug.slice(dateSlug.length + 1);

  const extension = ext.replace(/^\.+/, '').replace(/[^A-Za-z0-9]/g, '') || 'txt';
  return `${[label, day, slug].filter(Boolean).join('-')}.${extension}`;
}

function isAbortError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

function download(blob: Blob, fileName: string): void {
  if (typeof URL.createObjectURL !== 'function') {
    throw new Error('Brskalnik ne podpira prenosa datotek.');
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Revoking immediately cancels the download in some browsers (notably Safari).
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}

/**
 * Phones and tablets, where the share sheet is the natural way to send or save a file. Desktop
 * browsers that support file sharing (Safari on macOS) open a sheet without a "save to disk" option,
 * so there a plain download is used instead. iPadOS Safari reports itself as a Mac but has a coarse
 * (touch) primary pointer.
 */
function isHandheld(): boolean {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: unknown } }).userAgentData;
  if (typeof uaData?.mobile === 'boolean') return uaData.mobile;
  try {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * Trigger a browser download. With `share: true`, phones and tablets that can share the file open the
 * share sheet instead (e.g. iOS: Mail, "Shrani v Datoteke"). Resolves without doing anything when the
 * user closes the share sheet. Any other share failure – e.g. Safari's NotAllowedError when the user
 * gesture expired while the document was being generated – falls back to a normal download.
 * Inside the Android app the file is handed to the app (AndroidBridge), which saves or shares it:
 * a WebView cannot download blob: URLs by itself.
 */
export async function saveFile(blob: Blob, fileName: string, opts?: { share?: boolean }): Promise<void> {
  if (isAndroidApp()) {
    try {
      if (await saveFileInApp(blob, fileName, opts)) return;
    } catch {
      // The bridge failed: the app also intercepts plain downloads, try that.
    }
  }
  if (opts?.share && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && isHandheld()) {
    let file: File | null = null;
    try {
      file = new File([blob], fileName, { type: blob.type });
    } catch {
      file = null; // very old WebViews without the File constructor
    }
    let canShare = false;
    try {
      canShare = file !== null && navigator.canShare?.({ files: [file] }) === true;
    } catch {
      canShare = false;
    }
    if (file && canShare) {
      try {
        await navigator.share({ files: [file], title: fileName });
        return;
      } catch (err) {
        if (isAbortError(err)) return;
      }
    }
  }
  download(blob, fileName);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) return false;
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  // Off-screen but still selectable; 16px avoids the iOS zoom-on-focus.
  Object.assign(textarea.style, {
    position: 'fixed',
    top: '0',
    left: '-9999px',
    opacity: '0',
    fontSize: '16px',
  });
  document.body.appendChild(textarea);
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length); // iOS ignores select() on readonly fields
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
    active?.focus({ preventScroll: true });
  }
}

/** Copy text to the clipboard; resolves false when not permitted. Never throws. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or insecure context: try the legacy path below.
  }
  try {
    return legacyCopy(text);
  } catch {
    return false;
  }
}
